'use client';

import { useCallback } from 'react';
import { BN } from '@velocity-exchange/sdk';
import {
	getTokenAddressForDepositAndWithdraw,
	UIMarket,
} from '@velocity-exchange/common';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import { notify } from 'src/utils/notifications';
import { useGetTxParams } from '../transactions/useGetTxParams';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import { updateAfterTx } from 'src/stores/DriftStore/helpers/transactionHelpers';
import type { BaseMessageSignerWalletAdapter as Adapter } from '@solana/wallet-adapter-base';
import invariant from 'tiny-invariant';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignDriftActionWithFallback } from 'src/hooks/wallets/useSponsoredSignDriftActionWithFallback';

interface AddInsuranceFundStakeOptions {
	amount: BN;
	marketIndex: number;
	/**
	 * If true, also tries to create a new IF account at the same time. Use first time the user does it.
	 */
	initializeStakeAccount?: boolean;
	/**
	 * If true, tries to withdraw the amount from the current subaccount instead of using wallet balance
	 */
	fromSubaccount?: boolean;
	/**
	 * if fromSubaccount, pass userId of the subaccount
	 */
	userId?: number;
	/**
	 * if fromSubaccount, pass userKey of the subaccount
	 */
	userKey?: string;
}

export const useAddInsuranceFundStake = () => {
	const driftClient = useDriftStore((s) => s.driftClient.client);
	const wallet = useDriftStore((s) => s.wallet.current.adapter);
	const accountsGet = useDriftAccountsStore((s) => s.get);
	const getTxParams = useGetTxParams();

	const addInsuranceFundStakeAction = useCallback(
		async (
			signerWallet: UiEnhancedIWalletV2,
			opts: AddInsuranceFundStakeOptions
		) => {
			invariant(driftClient, 'driftClient is required');

			const spotMarketAccount = driftClient.getSpotMarketAccount(
				opts.marketIndex
			);

			if (!spotMarketAccount) {
				throw new Error('Spot market account not found');
			}

			const tokenAccount = await getTokenAddressForDepositAndWithdraw(
				spotMarketAccount,
				driftClient.wallet.publicKey
			);

			const ixs = await driftClient.getAddInsuranceFundStakeIxs({
				marketIndex: opts.marketIndex,
				amount: opts.amount,
				collateralAccountPublicKey: tokenAccount,
				initializeStakeAccount: opts.initializeStakeAccount,
				fromSubaccount: opts.fromSubaccount,
			});

			await signerWallet.signAndSendTransaction(ixs, getTxParams());
		},
		[driftClient, getTxParams]
	);

	const wrappedAddStake = useSponsoredSignDriftActionWithFallback(
		addInsuranceFundStakeAction
	);

	const addInsuranceFundStake = useCallback(
		async (opts: AddInsuranceFundStakeOptions): Promise<boolean> => {
			if (!driftClient || !wallet?.publicKey) return false;

			const accountsState = accountsGet();

			if (opts.userId !== undefined) {
				await driftClient.switchActiveUser(opts.userId);
			}

			const spotMarketConfig = UIMarket.spotMarkets.find(
				(m) => m.marketIndex === opts.marketIndex
			);

			const toastId = `add-stake-to-if-${opts.marketIndex}`;

			notify({
				id: toastId,
				type: 'awaiting_wallet_confirmation',
				showUntilCancelled: true,
			});

			try {
				await wrappedAddStake(opts);

				notify({
					id: toastId,
					type: 'success',
					message: `Staked ${spotMarketConfig?.symbol}.`,
					showUntilCancelled: false,
				});

				if (opts.userId !== undefined && opts.userKey) {
					const userClient = accountsState.accounts[opts.userKey]?.client;
					if (userClient) {
						updateAfterTx(driftClient, userClient);
					}
				}

				return true;
			} catch (err: any) {
				console.error(err);
				TransactionErrorHandler.handleError({
					error: err,
					toastId,
					wallet: wallet as Adapter,
				});
				return false;
			}
		},
		[driftClient, wallet, accountsGet, wrappedAddStake]
	);

	return { addInsuranceFundStake };
};
